import React, { useState } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Switch,
  FormControlLabel,

  Divider,
  Button,
  Alert,
  Tabs,
  Tab,
  useTheme,
  Paper,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
  IconButton,
  Tooltip,
} from '@mui/material';
import {
  Brightness4 as DarkModeIcon,
  BrightnessHigh as LightModeIcon,
  Contrast as ContrastIcon,
  Notifications as NotificationsIcon,
  Security as SecurityIcon,
  Language as LanguageIcon,
  Info as InfoIcon,
} from '@mui/icons-material';
import { useTheme as useCustomTheme } from '../hooks/useTheme';

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`settings-tabpanel-${index}`}
      aria-labelledby={`settings-tab-${index}`}
      {...other}
    >
      {value === index && <Box sx={{ py: 3 }}>{children}</Box>}
    </div>
  );
}

export default function SettingsPage() {
  const theme = useTheme();
  const { isDarkMode, isHighContrastMode, toggleTheme, toggleHighContrast } = useCustomTheme();
  const [tabValue, setTabValue] = useState(0);
  const [saved, setSaved] = useState(false);

  const handleTabChange = (_event: React.SyntheticEvent, newValue: number) => {
    setTabValue(newValue);
  };

  const handleSave = () => {
    // In a real app, this would save to backend
    setSaved(true);
    setTimeout(() => setSaved(false), 3000);
  };

  return (
    <Box>
      <Typography variant="h4" component="h1" gutterBottom>
      Settings
      </Typography>

      {saved && (
        <Alert severity="success" sx={{ mb: 2 }}>
          Settings saved successfully!
        </Alert>
      )}

      <Paper sx={{ width: '100%' }}>
        <Tabs value={tabValue} onChange={handleTabChange} aria-label="settings tabs">
          <Tab label="Appearance" />
          <Tab label="Notifications" />
          <Tab label="Security" />
          <Tab label="Language" />
        </Tabs>

        <TabPanel value={tabValue} index={0}>
          <Card>
            <CardContent>
              <Typography variant="h6" component="h2" gutterBottom>
                Theme Settings
              </Typography>
              <Typography
                variant="body2"
                sx={{
                  color: 'text.secondary',
                  mb: 3
                }}>
                Customize the appearance of the application
              </Typography>

              <List>
                <ListItem>
                  <ListItemText
                    primary="Dark Mode"
                    secondary="Use dark theme for reduced eye strain in low-light environments"
                  />
                  <ListItemSecondaryAction>
                    <FormControlLabel
                      control={
                        <Switch
                          checked={isDarkMode && !isHighContrastMode}
                          onChange={toggleTheme}
                          disabled={isHighContrastMode}
                          icon={<LightModeIcon />}
                          checkedIcon={<DarkModeIcon />}
                          aria-label="Dark Mode"
                        />
                      }
                      label=""
                    />
                  </ListItemSecondaryAction>
                </ListItem>

                <Divider component="li" role="listitem" />

                <ListItem>
                  <ListItemText
                    primary={
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        High Contrast Mode
                        <Tooltip title="WCAG AAA compliant theme with enhanced contrast ratios for better accessibility">
                          <IconButton size="small">
                            <InfoIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </Box>
                    }
                    secondary="Enable high contrast theme for improved visibility (WCAG AAA compliant)"
                  />
                  <ListItemSecondaryAction>
                    <FormControlLabel
                      control={
                        <Switch
                          checked={isHighContrastMode}
                          onChange={toggleHighContrast}
                          icon={<ContrastIcon />}
                          checkedIcon={<ContrastIcon />}
                          color="primary"
                          aria-label="High Contrast Mode"
                        />
                      }
                      label=""
                    />
                  </ListItemSecondaryAction>
                </ListItem>

                {isHighContrastMode && (
                  <ListItem>
                    <Alert severity="info" sx={{ width: '100%' }}>
                      High contrast mode is active. This theme meets WCAG AAA standards with:
                      <ul style={{ marginTop: 8, marginBottom: 0 }}>
                        <li>7:1 contrast ratio for normal text</li>
                        <li>4.5:1 contrast ratio for large text</li>
                        <li>Enhanced focus indicators</li>
                        <li>Bold borders for UI boundaries</li>
                      </ul>
                    </Alert>
                  </ListItem>
                )}
              </List>

              <Divider sx={{ my: 2 }} />

              <Typography variant="subtitle2" component="h3" gutterBottom>
                Preview
              </Typography>
              <Paper
                elevation={3}
                sx={{
                  p: 2,
                  mt: 1,
                  backgroundColor: theme.palette.background.paper,
                  border: isHighContrastMode ? '2px solid' : 'none',
                  borderColor: theme.palette.text.primary,
                }}
              >
                <Typography variant="body1" sx={{
                  marginBottom: '16px'
                }}>
                  This is how text appears with your current theme settings.
                </Typography>
                <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                  <Button variant="contained" size="small">
                    Primary Button
                  </Button>
                  <Button variant="outlined" size="small">
                    Outlined Button
                  </Button>
                  <Button variant="text" size="small">
                    Text Button
                  </Button>
                </Box>
              </Paper>
            </CardContent>
          </Card>
        </TabPanel>

        <TabPanel value={tabValue} index={1}>
          <Card>
            <CardContent>
              <Typography variant="h6" component="h2" gutterBottom>
                <NotificationsIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
                Notification Settings
              </Typography>
              <Typography variant="body2" sx={{
                color: 'text.secondary'
              }}>
                Configure how you receive notifications
              </Typography>
              {/* Add notification settings here */}
            </CardContent>
          </Card>
        </TabPanel>

        <TabPanel value={tabValue} index={2}>
          <Card>
            <CardContent>
              <Typography variant="h6" component="h2" gutterBottom>
                <SecurityIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
                Security Settings
              </Typography>
              <Typography variant="body2" sx={{
                color: 'text.secondary'
              }}>
                Manage your security preferences
              </Typography>
              {/* Add security settings here */}
            </CardContent>
          </Card>
        </TabPanel>

        <TabPanel value={tabValue} index={3}>
          <Card>
            <CardContent>
              <Typography variant="h6" component="h2" gutterBottom>
                <LanguageIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
                Language & Region
              </Typography>
              <Typography variant="body2" sx={{
                color: 'text.secondary'
              }}>
                Set your language and regional preferences
              </Typography>
              {/* Add language settings here */}
            </CardContent>
          </Card>
        </TabPanel>
      </Paper>

      <Box sx={{ mt: 3, display: 'flex', justifyContent: 'flex-end' }}>
        <Button variant="contained" onClick={handleSave}>
          Save Changes
        </Button>
      </Box>
    </Box>
  );
}
